import { useState, useEffect, useCallback, useContext } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { TravelRequest, ITravelRequest } from '@/lib/request-trip/models/travel-request.model';
import { toast } from '@/hooks/useToast';
import { AuthContext } from '@/lib/auth/models/auth.model';
import { useI18n } from '@/hooks/useI18n';


export const useTravelRequestsInfo = () => {
  const { t } = useI18n();
  const [travelRequests, setTravelRequests] = useState<TravelRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Recupera l'utente dal contesto di autenticazione
  const authContext = useContext(AuthContext);
  if (!authContext) {
    throw new Error('useTravelRequestsInfo deve essere usato all\'interno di un AuthProvider');
  }
  const { user } = authContext;

  const fetchTravelRequestsWithUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Recupera le travel requests dalla view che include i dati degli utenti
      const { data: requests, error: reqError } = await supabase
        .from('complete_travel_request_info')
        .select('*')
        .not('status', 'eq', 'rejected')
        .order('created_at', { ascending: false });

      if (reqError) {
        console.error('Error fetching travel requests:', reqError);
        setError('Errore nel caricamento delle richieste di viaggio');
        toast({
          title: t('common.error'),
          description: t('travelRequestMessages.loadError'),
          variant: "destructive",
        });
        return [];
      }

      const rows = requests || [];

      setTravelRequests(rows as unknown as TravelRequest[]);

      return rows;
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('Errore imprevisto nel caricamento delle richieste');
      toast({
        title: t('common.error'),
        description: t('travelRequestMessages.loadUnexpected'),
        variant: "destructive",
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh dei dati
  const refreshData = async () => {
    await fetchTravelRequestsWithUsers();
  };

  // Carica i dati all'inizializzazione
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        await fetchTravelRequestsWithUsers();
      } catch (err) {
        if (mounted) {
          console.error('Error initializing travel requests:', err);
        }
      }
    };
    load();
    return () => { mounted = false; };
  }, [fetchTravelRequestsWithUsers]);

  // Accetta una travel request assegnata
  const acceptTravelRequest = async (requestId: string) => {
    try {
      // Aggiorna lo status della richiesta a 'assigned' (già assegnata)
      const { error: updateError } = await supabase
        .from('travel_requests')
        .update({
          status: 'assigned',
          updated_at: new Date().toISOString(),
          assigned_designer_id: user.id
        })
        .eq('id', requestId);

      if (updateError) {
        console.error('Error accepting travel request:', updateError);
        toast({
          title: t('common.error'),
          description: t('travelRequestMessages.acceptError'),
          variant: "destructive",
        });
        return false;
      }

      // Ricarica i dati aggiornati
      await refreshData();

      toast({
        title: t('common.success'),
        description: t('travelRequestMessages.acceptSuccess'),
      });

      return true;
    } catch (err) {
      console.error('Unexpected error accepting request:', err);
      toast({
        title: t('common.error'),
        description: t('travelRequestMessages.acceptUnexpectedError'),
        variant: "destructive",
      });
      return false;
    }
  };

  // Rifiuta una travel request assegnata
  const declineTravelRequest = async (requestId: string) => {
    try {
      // Rimuovi l'assegnazione impostando assigned_designer_id a NULL e status a pending
      const { error: updateError } = await supabase
        .from('travel_requests')
        .update({
          status: 'rejected',
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId)
        .eq('assigned_designer_id', user.id);

      if (updateError) {
        console.error('Error declining travel request:', updateError);
        toast({
          title: t('common.error'),
          description: t('travelRequestMessages.declineError'),
          variant: "destructive",
        });
        return false;
      }

      // Ricarica i dati aggiornati
      await refreshData();

      toast({
          title: t('common.success'),
          description: t('travelRequestMessages.declineSuccess'),
      });

      return true;
    } catch (err) {
      console.error('Unexpected error declining request:', err);
      toast({
          title: t('common.error'),
          description: t('travelRequestMessages.declineUnexpectedError'),
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    travelRequests,
    loading,
    error,
    fetchTravelRequestsWithUsers,
    refreshData,
    acceptTravelRequest,
    declineTravelRequest,
  };
};
